import { useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Form, Field, FormSpy } from "react-final-form";

import Alert from "@components/Alert";
import Loading from "@components/Loading";
import InputText from "@components/Form/InputText";
import SelectSearch from "@components/Form/SelectSearch";
import {
  isRequired,
  composeFormValidators,
  isAlphaNumeric,
  isPhoneNumber,
  hasMaxLength,
} from "@lib/validations";
import Button from "@components/Button";
import { submitInStoreCreditRequest } from "@store/financing-solutions/actions";

const InStoreCreditRequestForm = ({
  submitInStoreCreditRequest,
  customer,
  stores,
}) => {
  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const formReset = useRef(null);

  const updateFormState = (subscription) => {
    if (subscription.active && isSent) {
      setIsSent(false);
    }
  };

  const handleFormSubmit = async (values) => {
    setIsSending(true);

    const formData = {
      firstname: values.firstName,
      lastname: values.lastName,
      phone: values.phone,
      store_location_id: values.store,
    };

    const response = await submitInStoreCreditRequest(formData);

    if (response) {
      setIsSent(true);
      setIsSending(false);

      formReset.current({
        firstName: customer && customer.first_name,
        lastName: customer && customer.last_name,
        phone: customer && customer.telephone,
        store: 0,
      });
    } else {
      setIsSending(false);
    }
  };

  return (
    <>
      <Form
        onSubmit={handleFormSubmit}
        initialValues={{
          firstName: customer && customer.first_name,
          lastName: customer && customer.last_name,
          phone: customer && customer.telephone,
          store: "",
        }}
        validate={(values) => {
          const errors = {};

          if (values.store && values.store === "") {
            errors.store = "Te rugam sa selectezi un magazin.";
          }
          return errors;
        }}
        render={({ handleSubmit, form }) => {
          formReset.current = form.reset;

          return (
            <form
              onSubmit={handleSubmit}
              id="credit_request_form"
              noValidate={true}
            >
              <FormSpy
                onChange={updateFormState}
                subscription={{ active: true }}
              />

              <div className="mb-4">
                <Field
                  component={InputText}
                  id="in_store_credit_request_first_name"
                  name="firstName"
                  label="Prenume"
                  className="Form-input Form-wide"
                  required
                  validate={composeFormValidators(
                    isRequired(),
                    isAlphaNumeric(),
                    hasMaxLength(60)
                  )}
                />
              </div>

              <div className="mb-4">
                <Field
                  component={InputText}
                  id="in_store_credit_request_last_name"
                  name="lastName"
                  label="Nume"
                  type="text"
                  required
                  validate={composeFormValidators(
                    isRequired(),
                    isAlphaNumeric(),
                    hasMaxLength(60)
                  )}
                />
              </div>

              <div className="mb-4">
                <Field
                  component={InputText}
                  id="in_store_credit_request_phone"
                  name="phone"
                  label="Telefon"
                  type="tel"
                  required
                  validate={composeFormValidators(
                    isRequired(),
                    isPhoneNumber()
                  )}
                />
              </div>

              <div className="mb-8">
                <Field
                  name="store"
                  id="in_store_credit_request_store"
                  component={SelectSearch}
                  label="Magazin"
                  required
                  validate={isRequired()}
                  className="Form-wide"
                  placeholder="Selecteaza un magazin"
                  parse={({ value }) => value}
                  format={(id) =>
                    id && stores
                      ? {
                          value: id,
                          label: stores.find((store) => store.id === id).name,
                        }
                      : null
                  }
                  options={
                    stores &&
                    stores.map(({ id, name }) => ({ value: id, label: name }))
                  }
                />
              </div>

              <div className="mt-4 mb-6 xs:hidden max-w-[390px]">
                <Button
                  type="submit"
                  title="Vreau sa fiu contactat"
                  display="block"
                />
              </div>

              <div className="mx-auto mt-4 mb-6 hidden xs:block">
                <Button
                  type="submit"
                  title="Vreau sa fiu contactat"
                  display="inline"
                />
              </div>

              {isSending && <Loading height={80}>Se trimite</Loading>}
            </form>
          );
        }}
      />

      {/* Sent form */}
      {isSent && (
        <div className="mb-4 text-center">
          <Alert type="success" showIcon>
            Formularul a fost trimis.
          </Alert>
        </div>
      )}
    </>
  );
};

InStoreCreditRequestForm.propTypes = {
  customer: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    telephone: PropTypes.string,
  }),
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onFormSubmit: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    customer: state.user.customer,
    stores: state.stores.data,
  }),
  {
    submitInStoreCreditRequest,
  }
)(InStoreCreditRequestForm);
